import type { FileForProcessing, ISamplePool, Sample } from '../../../../Model/index.js'
import Papa from 'papaparse'
import { getSequenceIdFromFilename } from '../../../../Util/SampleFileUtils.js'
import { poolDisplayName } from '../../OrderManagementPage/PoolName.tsx'

/* Intl rounds to 3 fraction digits by default, and the grid writes what it shows back on save. */
const numberFormatter = new Intl.NumberFormat(navigator.language, {
  useGrouping: false,
  maximumFractionDigits: 20
})

const formatNumber = (value?: number) => (value !== undefined ? numberFormatter.format(value) : '')

function longestCommonPrefix(str1: string, str2: string): string {
  const minLength = Math.min(str1.length, str2.length)
  let i = 0

  while (i < minLength && str1[i] === str2[i]) {
    i++
  }

  return str1.slice(0, i)
}

export interface PoolCsvTable {
  columns: string[]
  rows: Record<string, string>[]
}

const bySequenceId = (a: Record<string, string>, b: Record<string, string>) =>
  a.sequenceId.localeCompare(b.sequenceId, undefined, { numeric: true, sensitivity: 'base' })

const sequenceIdOf = (sample: FileForProcessing) =>
  longestCommonPrefix(
    getSequenceIdFromFilename(sample.forwardFile?.fileName ?? ''),
    getSequenceIdFromFilename(sample.reverseFile?.fileName ?? '')
  )

/**
 * The editable row for one sample. Column order is the literal's key order, since
 * {@link collectCsvColumns} walks rows in insertion order. Order rows name their pool so an
 * export-edit-import round trip keeps it; project rows have no pools.
 */
const sampleCsvRow = (sample: FileForProcessing, poolName?: string): Record<string, string> => {
  const row: Record<string, string> = {
    sampleId: sample.id,
    customerSampleName: sample.customerSampleName ?? '',
    sequenceId: sequenceIdOf(sample),
    forwardFile: sample.forwardFile?.fileName ?? '',
    reverseFile: sample.reverseFile?.fileName ?? '',
    type: 'sample',
    ...(poolName === undefined ? {} : { pool: poolName }),
    forwardPrimer: sample.forwardPrimer ?? '',
    reversePrimer: sample.reversePrimer ?? '',
    forwardPrimerRequested: sample.sampleMetadata?.forwardPrimerRequested ?? '',
    reversePrimerRequested: sample.sampleMetadata?.reversePrimerRequested ?? '',
    lat: formatNumber(sample.sampleMetadata?.sampleCoordinates?.coordinates[1]),
    lon: formatNumber(sample.sampleMetadata?.sampleCoordinates?.coordinates[0]),
    samplingDate: sample.sampleMetadata?.samplingDate ?? '',
    samplingStart: sample.sampleMetadata?.samplingStart ?? '',
    samplingEnd: sample.sampleMetadata?.samplingEnd ?? '',
    matrixType: sample.sampleMetadata?.matrixType ?? '',
    extractionKit: sample.sampleMetadata?.extractionKit ?? '',
    trapType: sample.sampleMetadata?.trapType ?? '',
    collectionType: sample.sampleMetadata?.collectionType ?? '',
    sequencer: sample.sampleMetadata?.sequencer ?? '',
    sequencingChemistry: sample.sampleMetadata?.sequencingChemistry ?? '',
    sequencingDepth: sample.sampleMetadata?.sequencingDepth ?? '',
    localityType: sample.sampleMetadata?.localityType ?? '',
    habitatType: sample.sampleMetadata?.habitatType ?? ''
  }

  Object.entries(sample.sampleMetadata?.customAttributes ?? {}).forEach(([key, value]) => {
    if (row[key] === undefined) {
      row[key] = value
    }
  })

  return row
}

export const buildSamplePoolCsvRows = (samplePools: ISamplePool[]): Record<string, string>[] => {
  const rows: Record<string, string>[] = []

  samplePools.forEach((pool, index) => {
    const poolName = poolDisplayName(pool, index)
    const sampleRows = pool.samples.map((sample) => sampleCsvRow(sample, poolName))
    const ncRows = pool.negativeControls.map((nc) => ({
      sampleId: nc.id,
      customerSampleName: nc.customerSampleName ?? '',
      sequenceId: sequenceIdOf(nc),
      forwardFile: nc.forwardFile?.fileName ?? '',
      reverseFile: nc.reverseFile?.fileName ?? '',
      type: 'negative_control',
      pool: poolName,
      forwardPrimer: nc.forwardPrimer ?? '',
      reversePrimer: nc.reversePrimer ?? ''
    }))

    sampleRows.sort(bySequenceId)
    ncRows.sort(bySequenceId)

    rows.push(...sampleRows, ...ncRows)
  })

  return rows
}

export const collectCsvColumns = (rows: Record<string, string>[]): string[] => {
  const seen = new Set<string>()
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => seen.add(key))
  })
  return [...seen]
}

const normalizeTable = (rows: Record<string, string>[]): PoolCsvTable => {
  const columns = collectCsvColumns(rows)

  const normalizedRows = rows.map((row) => {
    const normalized: Record<string, string> = {}
    columns.forEach((column) => {
      normalized[column] = row[column] ?? ''
    })
    return normalized
  })

  return { columns, rows: normalizedRows }
}

export const buildSamplePoolCsvTable = (samplePools: ISamplePool[]): PoolCsvTable =>
  normalizeTable(buildSamplePoolCsvRows(samplePools))

/**
 * Project samples are a flat cross-order list with no pools and no negative controls, and the caller
 * has already narrowed them to the ones this user may write to — see `ProjectSampleWidget`.
 */
export const buildProjectSampleCsvTable = (samples: Sample[]): PoolCsvTable =>
  normalizeTable([...samples.map((sample) => sampleCsvRow(sample))].sort(bySequenceId))

export const poolCsvRowsToCsv = (rows: Record<string, string>[]) => {
  return Papa.unparse(rows, { delimiter: ';' })
}
